// @flow
import React from 'react';
import {
  useCustomValidation,
  usePropsValidation,
} from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import { RowContext } from './grid.js';
import Figure from './Figure.js';
import { useValidationApiOrigin } from './validation/ValidationApiOriginContext.js';

// aranged in Map<Width, Height[]>
const VALID_DIMENSIONS: { [string]: number[] } = {
  '260': [260],
  '596': [260, 360, 596],
};

const dimensionStrings = [];
for (const width of Object.keys(VALID_DIMENSIONS)) {
  const heights = VALID_DIMENSIONS[width];
  for (const height of heights) dimensionStrings.push(`${width}×${height}`);
}

const DIMENSION_ERROR_MESSAGE = `The geogebra component can only be one of the following dimensions (W×H): ${dimensionStrings.join(
  ', ',
)}`;

type Props = {|
  attribution?: {|
    author: string,
    materialUrl: string,
  |},
  materialId: string,
  height: number,
  width: number,
  ...CompilerInjectedProps,
|};

export default function GeogebraIframe(props: Props) {
  const { failedValidation, propValidationStatus, addErrors, locations } =
    usePropsValidation(props, GeogebraIframe);
  const { pixelWidth } = React.useContext(RowContext);
  const { failedValidation: failedCustomValidation } = useCustomValidation(
    props,
    (addErrors, locations) => {
      if (propValidationStatus.attribution && props.attribution != null) {
        // validate material url to be the correct type
        const re = new RegExp(
          '^https:\\/\\/(www\\.)?geogebra\\.org\\/m\\/[^\\/]+$',
        );
        if (props.attribution.materialUrl.match(re) == null) {
          addErrors([
            {
              message: `Attribution urls should be properly formatted geogebra urls https://www.geogebra.org/m/<id>`,
              ...locations.attribution,
            },
          ]);
        }
      }

      // validate height and width combinations
      const validHeights = VALID_DIMENSIONS[String(props.width)];
      if (validHeights == null || !validHeights.includes(props.height))
        addErrors([{ message: DIMENSION_ERROR_MESSAGE, ...locations.width }]);

      if (props.width != null && props.width > pixelWidth)
        addErrors([
          {
            message: `Geogebra iframes should have widths smaller than their containers. Max width: ${pixelWidth}`,
            ...locations.width,
          },
        ]);
    },
  );

  // Using ad-hoc assertion (for now) due to async nature of this check.
  const apiOrigin = useValidationApiOrigin();
  React.useEffect(() => {
    if (!props._performValidation) return;
    // We'll avoid doing the checks until we know our dimensions and materialId are valid.
    // Could do more fine-grained checks here in future.
    if (failedValidation || failedCustomValidation) return;

    // NOTE there will be race conditions here due to non-cancellation of async promise chain
    // Worst case, you get an error showing up for a stale document and have to force
    // a new render cycle to clear it out.
    // TODO solve minor race conditions.
    (async function verifyGeogebraMaterial() {
      // TODO handle network errors / response errors properly.
      const res = await fetch(
        `${apiOrigin}/api/geogebra-material?materialId=${props.materialId}`,
      );
      const json = await res.json();
      const { item } = json.responses.response[1];
      if (item === undefined) {
        addErrors([
          {
            message: `The Geogebra material could not be found. Make sure you are using a valid material id.`,
            ...locations.materialId,
          },
        ]);
        return;
      }
      const { width, height, visibility, author } = item;
      if (
        Number.parseFloat(width) !== props.width ||
        Number.parseFloat(height) !== props.height
      ) {
        addErrors([
          {
            message: `The Geogebra material must be made for the dimensions you are trying to use (${props.width}×${props.height}). Instead it is has been made for: ${width}×${height}.`,
            ...locations.materialId,
          },
        ]);
      }

      if (visibility !== 'S') {
        addErrors([
          {
            message: `The Geogebra material must have the sharing setting set to: "Shared with Link". It is currently set to "${
              visibility === 'O'
                ? 'Public'
                : visibility === 'P'
                  ? 'Private'
                  : visibility
            }"`,
            ...locations.materialId,
          },
        ]);
      }

      if (author !== 'mathspace') {
        addErrors([
          {
            message: `The Geogebra material must be owned/authored by the mathspace user account. The author of the specified material is: "${author}"`,
            ...locations.materialId,
          },
        ]);
      }
    })();
  });

  if (failedValidation || failedCustomValidation) return null;
  return (
    <Figure>
      <div style={{ textAlign: 'center' }}>
        <iframe
          title="geogebra"
          scrolling="no"
          src={`https://www.geogebra.org/material/iframe/id/${props.materialId}/width/${props.width}/height/${props.height}/border/888888/rc/false/ai/false/sdz/false/smb/false/stb/false/stbh/true/ld/false/sri/true/at/preferhtml5`}
          width={props.width}
          height={props.height}
          style={{ border: 'none' }}
        />
        <div style={{ display: 'inline-table', width: props.width }}>
          <p style={{ display: 'table-cell', textAlign: 'left' }}>
            Created with: <a href="https://www.geogebra.org">© Geogebra</a>
          </p>
          {props.attribution != null && (
            <p style={{ display: 'table-cell', textAlign: 'right' }}>
              adapted from original by{' '}
              <a href={props.attribution.materialUrl}>
                {props.attribution.author}
              </a>
            </p>
          )}
        </div>
      </div>
    </Figure>
  );
}

GeogebraIframe.spec = {
  materialId: t.string,
  height: t.number,
  width: t.number,
  attribution: t.optional(
    t.exact({
      author: t.string,
      materialUrl: t.string,
    }),
  ),
};
